import React, {useCallback, useEffect, useState} from 'react';
import {FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiEdit3, FiEye, FiLoader, FiMessageSquare, FiRefreshCw, FiX} from 'react-icons/fi';
import {examFeedbackApi, getErrorMessage} from '../services/api';
import AppealDetails from './AppealDetails';
import {
    appealReasonLabels,
    appealStatusLabels,
    canEditFeedback,
    feedbackCategoryLabels,
    feedbackEditDeadline,
    feedbackScopeLabels,
    formatFeedbackDate,
    formatRemainingTime,
    getConflictCode,
    toFeedbackPage,
} from './examFeedbackUtils';

const editableFields = (item) => ({
    category: item.category,
    rating: item.rating,
    comment: item.comment || '',
    consent_for_improvement: Boolean(item.consent_for_improvement),
});

export default function StudentFeedbackHistory({onResultRefresh, standalone = false}) {
    const [expanded, setExpanded] = useState(standalone);
    const [hasUpdates, setHasUpdates] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const [tab, setTab] = useState('appeals');
    const [page, setPage] = useState(1);
    const [data, setData] = useState(toFeedbackPage());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedAppeal, setSelectedAppeal] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const lastTriggerRef = React.useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = tab === 'appeals'
                ? await examFeedbackApi.student.appeals({page, page_size: 10})
                : await examFeedbackApi.student.feedback({page, page_size: 10});
            setData(toFeedbackPage(response));
            setHasUpdates(false);
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setLoading(false);
        }
    }, [tab, page]);

    useEffect(() => { if (expanded) load(); }, [expanded, load]);
    useEffect(() => { if (standalone) setExpanded(true); }, [standalone]);
    useEffect(() => {
        if (!expanded) return undefined;
        setNow(Date.now());
        const timer = window.setInterval(() => setNow(Date.now()), 60000);
        return () => window.clearInterval(timer);
    }, [expanded]);
    useEffect(() => {
        const refresh = () => expanded ? load() : setHasUpdates(true);
        window.addEventListener('exam-feedback-changed', refresh);
        return () => window.removeEventListener('exam-feedback-changed', refresh);
    }, [expanded, load]);

    useEffect(() => {
        if (!selectedAppeal && !editing) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = (event) => {
            if (event.key !== 'Escape' || saving) return;
            setSelectedAppeal(null);
            setEditing(null);
            window.setTimeout(() => lastTriggerRef.current?.focus(), 0);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [selectedAppeal, editing, saving]);

    const changeTab = (next) => { setTab(next); setPage(1); setError(''); };
    const openAppeal = async (item, trigger) => {
        lastTriggerRef.current = trigger;
        setSelectedAppeal(item);
        setDetailLoading(true);
        setDetailError('');
        try {
            setSelectedAppeal(await examFeedbackApi.student.appealDetail(item.id));
        } catch (requestError) {
            setDetailError(getErrorMessage(requestError));
        } finally {
            setDetailLoading(false);
        }
    };
    const withdraw = async () => {
        if (!selectedAppeal || !window.confirm('از این اعتراض منصرف می‌شوید؟')) return;
        setSaving(true);
        setDetailError('');
        try {
            const updated = await examFeedbackApi.student.withdrawAppeal(selectedAppeal.id, selectedAppeal.revision);
            setSelectedAppeal(updated);
            await load();
            onResultRefresh?.();
        } catch (requestError) {
            if (getConflictCode(requestError) === 'APPEAL_REVISION_CONFLICT') {
                const current = await examFeedbackApi.student.appealDetail(selectedAppeal.id);
                setSelectedAppeal(current);
                setDetailError('پرونده هم‌زمان تغییر کرده است؛ آخرین نسخه نمایش داده شد و درخواست قبلی ارسال نشد.');
            } else setDetailError(getErrorMessage(requestError));
        } finally {
            setSaving(false);
        }
    };
    const startEdit = (item, trigger) => { lastTriggerRef.current = trigger; setEditing(item); setForm(editableFields(item)); };
    const closeDialog = () => {
        if (saving) return;
        setSelectedAppeal(null);
        setEditing(null);
        window.setTimeout(() => lastTriggerRef.current?.focus(), 0);
    };
    const saveFeedback = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            await examFeedbackApi.student.updateFeedback(editing.id, {...form, rating: Number(form.rating), comment: form.comment.trim()});
            setEditing(null);
            setForm(null);
            await load();
        } catch (requestError) {
            setError(requestError?.status === 403 ? 'مهلت ۲۴ ساعته ویرایش این بازخورد پایان یافته است.' : getErrorMessage(requestError));
        } finally {
            setSaving(false);
        }
    };

    const items = data.results || [];
    const pageCount = Math.max(1, Math.ceil((data.count || 0) / 10));
    return <section className={`exam-panel ef-history ${expanded ? 'expanded' : 'collapsed'} ${standalone ? 'standalone' : ''}`}>
        {standalone ? <div className="ef-history-toggle ef-history-heading"><span><FiMessageSquare/><span><strong>درخواست‌های من</strong><small>بازخوردها، اعتراض‌ها و نتیجه رسیدگی</small></span></span>{hasUpdates && <i>جدید</i>}</div> :
        <button className="ef-history-toggle" type="button" aria-expanded={expanded} onClick={() => setExpanded((current) => !current)}><span><FiMessageSquare/><span><strong>درخواست‌های من</strong><small>بازخوردها، اعتراض‌ها و نتیجه رسیدگی</small></span></span><span>{hasUpdates && <i>جدید</i>}{expanded ? <FiChevronUp/> : <FiChevronDown/>}</span></button>}
        {expanded && <>
        <div className="ef-history-toolbar"><span>{data.count ? `${data.count} مورد در این بخش` : 'پیگیری وضعیت درخواست‌های ثبت‌شده'}</span><button className="exam-btn ghost" onClick={load} aria-label="تازه‌سازی"><FiRefreshCw className={loading ? 'spin' : ''}/></button></div>
        <div className="ef-tabs"><button className={tab === 'appeals' ? 'active' : ''} onClick={() => changeTab('appeals')}>اعتراض‌ها</button><button className={tab === 'feedback' ? 'active' : ''} onClick={() => changeTab('feedback')}>بازخوردها</button></div>
        {error && <div className="ef-message error" role="alert">{error}</div>}
        {loading ? <div className="ef-loading"><FiLoader className="spin"/> در حال دریافت…</div> : items.length ? <div className="ef-history-list">
            {tab === 'appeals' ? items.map((item) => <article key={item.id}>
                <div><span className={`ef-status ${item.status}`}>{appealStatusLabels[item.status] || item.status}</span><h3>{item.snapshot?.exam_title || `آزمون ${item.exam_id}`}</h3><p>{appealReasonLabels[item.reason] || item.reason} · {formatFeedbackDate(item.submitted_at)}</p></div>
                <button className="exam-btn ghost" onClick={(event) => openAppeal(item, event.currentTarget)}><FiEye/> جزئیات</button>
            </article>) : items.map((item) => <article key={item.id}>
                <div><span className="ef-status feedback">{feedbackScopeLabels[item.scope] || item.scope}</span><h3>{item.snapshot?.exam_title || `آزمون ${item.exam_id}`}</h3><p>{feedbackCategoryLabels[item.category] || item.category} · امتیاز {item.rating} از ۵ · {formatFeedbackDate(item.created_at)}</p></div>
                {canEditFeedback(item, now) ? <button className="exam-btn ghost" onClick={(event) => startEdit(item, event.currentTarget)}><FiEdit3/> {formatRemainingTime(feedbackEditDeadline(item.created_at), now)}</button> : <small>مهلت ویرایش پایان یافته</small>}
            </article>)}
        </div> : <div className="ef-empty"><FiMessageSquare/><strong>موردی ثبت نشده است.</strong></div>}
        {pageCount > 1 && <div className="ef-pagination"><button disabled={!data.previous} onClick={() => setPage((current) => current - 1)}><FiChevronRight/></button><span>{page} از {pageCount}</span><button disabled={!data.next} onClick={() => setPage((current) => current + 1)}><FiChevronLeft/></button></div>}
        </>}
        {selectedAppeal && <div className="ef-overlay" onClick={closeDialog}><div className="ef-modal ef-modal-wide" role="dialog" aria-modal="true" aria-labelledby="student-appeal-title" onClick={(event) => event.stopPropagation()}><header><div><div><h2 id="student-appeal-title">پرونده اعتراض #{selectedAppeal.id}</h2><p>آخرین بروزرسانی: {formatFeedbackDate(selectedAppeal.updated_at)}</p></div></div><button onClick={closeDialog} aria-label="بستن"><FiX/></button></header>{detailLoading ? <div className="ef-loading"><FiLoader className="spin"/></div> : <><AppealDetails appeal={selectedAppeal}/>{detailError && <div className="ef-message error" role="alert">{detailError}</div>}{['submitted', 'under_review'].includes(selectedAppeal.status) && <footer><button className="danger" disabled={saving} onClick={withdraw}>انصراف از اعتراض</button></footer>}</>}</div></div>}
        {editing && form && <div className="ef-overlay" onClick={closeDialog}><div className="ef-modal" role="dialog" aria-modal="true" aria-labelledby="student-feedback-edit-title" onClick={(event) => event.stopPropagation()}><header><div><div><h2 id="student-feedback-edit-title">ویرایش بازخورد</h2><p>{formatRemainingTime(feedbackEditDeadline(editing.created_at), now)} · تا {formatFeedbackDate(feedbackEditDeadline(editing.created_at))}</p></div></div><button onClick={closeDialog} aria-label="بستن"><FiX/></button></header><form onSubmit={saveFeedback}><label>موضوع<select value={form.category} onChange={(event) => setForm({...form, category: event.target.value})}>{Object.entries(feedbackCategoryLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label><label>امتیاز<select value={form.rating} onChange={(event) => setForm({...form, rating: event.target.value})}>{[1, 2, 3, 4, 5].map((rating) => <option key={rating} value={rating}>{rating} از ۵</option>)}</select></label><label>توضیحات<textarea rows="5" minLength="3" maxLength="4000" required value={form.comment} onChange={(event) => setForm({...form, comment: event.target.value})}/></label><label className="ef-check"><input type="checkbox" checked={form.consent_for_improvement} onChange={(event) => setForm({...form, consent_for_improvement: event.target.checked})}/><span>اجازه استفاده برای بهبود سامانه</span></label><footer><button type="button" onClick={closeDialog}>انصراف</button><button className="primary" disabled={saving}>ذخیره تغییرات</button></footer></form></div></div>}
    </section>;
}

